import { decamelizeKeys } from 'humps';

import { apiClient } from '../apiClient';

export const PATHS = {
  ASSIGNED_SCREENERS: '/api/v1/assigned-screeners',
  // get_or_create + start so a single POST returns the started instance with
  // its question group (mirrors Sparta's postProgramUnitInstance).
  PROGRAM_UNIT_INSTANCE: '/api/v4/program-unit-instance/?get_or_create=true&start=true',
  SURVEY_CUSTOMER_RESPONSE: '/api/v3/surveycustomerresponse/?mark_survey_completed=true',
} as const;

// Legacy ProgramUnit keys for the assigned screeners that are served via the
// dynamic-assessment flow in the test arm. Passed as `exclude_screeners` so the
// server (when its screener_exclude_override flag is on) strips them from the
// legacy response — preventing a double-ask when the legacy flow runs AFTER the
// dynamic pass to serve any remaining (unmigrated) screeners.
// Note: these differ from the dynamic flow keys (see DYNAMIC_ONBOARDING_FLOW_KEYS) —
// the refill, GLP-1, state, and lab-preference keys carry a `_screener` segment
// here ('aom_refill_date_v1' / 'aom_specific_glp1_v1' / 'us_state_v1' /
// 'lab_preference_v1' dynamically).
// Dynamic flows with NO exclude partner here on purpose:
//   - `pre_diabetes_v1` and `phq_gad_v1` are the pre-diabetes / PHQ-GAD
//     questionnaires, which live on their own endpoints + screens
//     (PreDiabetesScreen / PhqGadScreen), not `assigned_screeners`;
//   - `diabetes_decision_v1` DOES map to legacy assigned screeners
//     ('diabetes_type_screener_v1', 'insulin_pump_screener_v1',
//     'diabetes_medication_management_consent_screener_v1'), but its terminal's
//     `write_legacy_screener_response` effect completes those ProgramUnitInstances
//     server-side, so `_maybe_assign_*` already drops them from the legacy list
//     (`_user_has_already_completed_screener`) — no client-side exclude needed.
//     One nuance, scoped to THIS flow: that write-back is inline only for the
//     COE-critical specs, and `insulin_pump_screener_v1` is `coe_critical=False`
//     (`legacy_sync/diabetes.py`), so it is deferred to the async full sync and
//     can lag the other two. The converter is registered solely under
//     `diabetes_decision_v1` (`legacy_sync/registry.py`), so none of this applies
//     to `diabetes_inclusion_v1`, which syncs nothing at all — see below.
// These five are safe to exclude unconditionally on arm membership alone: each
// one's dynamic flow, once published, either completes the legacy
// ProgramUnitInstance server-side or was never reachable as a double-ask in the
// first place. See DIABETES_INCLUSION_LEGACY_SCREENER_KEYS below for the three
// keys that do NOT have that property and need a per-flow-completion gate.
export const DYNAMIC_LEGACY_SCREENER_KEYS = [
  'aom_screener_v1',
  'aom_refill_date_screener_v1',
  'aom_specific_glp1_screener_v1',
  'us_state_screener_v1',
  'lab_preference_screener_v1',
] as const;

// `diabetes_inclusion_v1` covers the same legacy type/pump/consent territory as
// `diabetes_decision_v1`, for acuity routing, but unlike `diabetes_decision_v1`
// it has NO terminal effects — it feeds COE directly
// (`diabetes_inclusion_routing.py`) and writes nothing back to the legacy
// tables, so these ProgramUnitInstances never get completed server-side and
// `_maybe_assign_*` keeps assigning them if excluded on the client side too.
//
// Unlike the five keys above, these must NOT be excluded on arm membership
// alone — the caller must additionally confirm `diabetes_inclusion_v1` actually
// appears in the session's `completedFlowKeys` before passing this list. An
// abandoned or unpublished flow is fail-safe by design elsewhere in this repo
// (see the `preDiabetesServed` / `phqGadServed` pattern in
// RulesEngineContext.tsx): a flow that never completed must not silently
// suppress its legacy fallback.
//
// The webserver Split `diabetes_inclusion_dynamic_assessment` also suppresses
// these three, but it can't be used while the dynamic-assessment rollout is
// partial: it runs in AssignedScreenerViewV1, which BOTH arms hit, so turning
// it on strips the legacy screeners for control-arm members too — and they
// never reach `diabetes_inclusion_v1` to replace them. Split and PostHog also
// bucket independently, so matching percentages would still be different
// member sets. Excluding client-side, gated on completion, scopes the
// suppression to members actually served (and who finished) the dynamic flow,
// which is what the rollout needs.
//
// One case still diverges even when gated on completion:
// `completed_high_acuity` (medication_max_med_score >= 4) has diabetes but asks
// nothing, so excluding it would leave that member unasked in both arms.
// Currently unreachable — MMS always reads None on prod because the Feature
// Store read exceeds EXTERNAL_STATE_READ_TIMEOUT_MS (300ms vs ~900ms actual).
// If that timeout is ever raised, revisit this: the exclude would need to
// exempt that specific terminal outcome too.
export const DIABETES_INCLUSION_LEGACY_SCREENER_KEYS = [
  'diabetes_type_screener_v1',
  'insulin_pump_screener_v1',
  'diabetes_medication_management_consent_screener_v1',
] as const;

// One backend-assigned screener. The list is consumed one at a time — only the
// FIRST entry is ever shown; after it's answered the endpoint is re-fetched (the
// backend re-sorts based on prior answers) until it returns empty. Mirrors
// Sparta's getAssignedScreeners flow.
export type AssignedScreener = {
  key: string;
  uuid: string;
};

export type ScreenerAnswerChoice = {
  resourceUri: string;
  text: string;
  description: string | null;
};

export type ScreenerQuestion = {
  uuid: string;
  text: string;
  additionalInfo: string;
  questionType: string;
  answerChoices: ScreenerAnswerChoice[];
};

// The started program-unit instance for a screener — carries the question group
// to render. Only the fields onboarding consumes are typed (camelized by the
// apiClient response interceptor).
export type ProgramUnitInstance = {
  resourceUri: string;
  programUnit: {
    internalName: string;
    contentCards: {
      questionGroup: {
        resourceUri: string;
        questions: ScreenerQuestion[];
      };
    }[];
  };
};

export type SurveyResponseObject = {
  user: string;
  question: string;
  questionGroup: string;
  answer: string | null;
  freeResponse: string | null;
  programUnitInstance: string;
};

export type SurveyResponsePayload = {
  objects: SurveyResponseObject[];
};

export type FetchAssignedScreenersOptions = {
  /**
   * Screener keys to strip from the response via `exclude_screeners` (server
   * honors this when its own override flag is on). Pass the migrated legacy keys
   * (DYNAMIC_LEGACY_SCREENER_KEYS) when the legacy flow runs after the dynamic
   * pass so those screeners aren't re-asked. Omitted/empty → no exclusion (the
   * control-arm default).
   */
  excludeKeys?: readonly string[];
};

export async function fetchAssignedScreeners(
  signal?: AbortSignal,
  options?: FetchAssignedScreenersOptions
): Promise<AssignedScreener[]> {
  const excludeKeys = options?.excludeKeys;
  const res = await apiClient.get<AssignedScreener[]>(PATHS.ASSIGNED_SCREENERS, {
    signal,
    ...(excludeKeys && excludeKeys.length > 0
      ? { params: { exclude_screeners: excludeKeys.join(',') } }
      : {}),
  });
  return Array.isArray(res.data) ? res.data : [];
}

// Starts (or fetches) the program-unit instance for a screener and returns it
// with its question group. Sparta keys this off the screener's uuid as the
// program-unit uri. Returns undefined if the backend sends back no instance.
export async function createProgramUnitInstance(
  programUnitUuid: string
): Promise<ProgramUnitInstance | undefined> {
  const res = await apiClient.post<ProgramUnitInstance | ProgramUnitInstance[]>(
    PATHS.PROGRAM_UNIT_INSTANCE,
    decamelizeKeys({
      programUnitUri: `/api/v4/program-unit/${programUnitUuid}/`,
      programUnitType: 'tool',
      senderReason: 'screener-onboarding',
    })
  );
  // The endpoint returns the started instance as a single object; tolerate an
  // array too (Sparta's older shape).
  const data = res.data;
  return Array.isArray(data) ? data[0] : (data ?? undefined);
}

// Submits the answers for a screener's question group. Mirrors Sparta's
// patchSurveyAnswers — only the status code matters, the body is ignored.
export async function submitSurveyResponses(payload: SurveyResponsePayload): Promise<void> {
  await apiClient.patch(PATHS.SURVEY_CUSTOMER_RESPONSE, decamelizeKeys(payload));
}
